import React from 'react';
import { TbAlertTriangle, TbCircleCheck } from 'react-icons/tb';

import { HStack, Icon, Tooltip } from '../../core-ui';
import { TableView } from '../../core-ui/TableView';
import formatCurrency from '../../core/support/formatCurrency';
import formatDate from '../../core/support/formatDate';
import { OptionsMap } from '../../support/helpers/CategoryOptions';
import { useNavPersistedState } from '../Navigation';

import { StatusBadge } from './VendorStatus';
import { TagView } from './VendorTag';
import {
  GetVendorList_season_vendors as Vendor,
  GetVendorList_season_tags as Tag,
} from './__generated__/GetVendorList';

export function VendorListView(props: {
  optionsMap: OptionsMap;
  vendors: Array<Vendor>;
  tags: Array<Tag>;
  selected: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  onVendorClick: (vendor: Vendor) => void;
}) {
  const {
    optionsMap,
    vendors,
    tags,
    selected,
    onSelectionChange,
    onVendorClick,
  } = props;

  const [sortBy, setSortBy] = useNavPersistedState<[number, boolean]>(
    'sort-state',
    // Initially sort by column 3 (signup date) descending
    [3, true],
  );

  const tagMap = new Map(tags.map((tag) => [tag.id, tag]));

  return (
    <TableView
      data={vendors}
      selected={selected}
      onSelectionChange={onSelectionChange}
      onRowClick={onVendorClick}
      sortBy={sortBy}
      onSortChange={setSortBy}
      columns={[
        {
          title: t('Name'),
          render: (vendor) => vendor.name,
          tdProps: { maxWidth: 300 },
          sortable: { sortFn: (a, b) => a.name.localeCompare(b.name) },
        },
        {
          title: t('Contact'),
          render: (vendor) => vendor.contact,
          tdProps: { maxWidth: 300 },
          sortable: { sortFn: (a, b) => a.contact.localeCompare(b.contact) },
        },
        {
          title: t('Category'),
          render: (vendor) =>
            vendor.category
              .map((id) => optionsMap.get(id)?.name ?? '')
              .join(','),
        },
        {
          title: t('Signup Date'),
          render: (vendor) => formatDate(vendor.createdAt),
          tdProps: { whiteSpace: 'nowrap' },
          sortable: {
            sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
          },
        },
        {
          title: t('Status'),
          render: (vendor) => <StatusBadge status={vendor.status} />,
          sortable: { sortFn: (a, b) => a.status.localeCompare(b.status) },
        },
        {
          title: t('Paid'),
          render: (vendor) => {
            const balance = formatCurrency(vendor.balance * 100);
            const roundedBalance = Math.round(vendor.balance * 100);
            return (
              <Tooltip
                label={t('Balance owed: {balance}', { balance })}
                placement="top"
              >
                <HStack>
                  {roundedBalance > 0 ? (
                    <Icon as={TbAlertTriangle} fontSize={20} color="red.500" />
                  ) : (
                    <Icon as={TbCircleCheck} fontSize={20} color="green.500" />
                  )}
                </HStack>
              </Tooltip>
            );
          },
        },
        {
          title: t('Tags'),
          render: (vendor) => (
            <HStack spacing={1}>
              {vendor.tags.map(({ id }) => {
                const tag = tagMap.get(id);
                return tag ? <TagView key={id} tag={tag} /> : null;
              })}
            </HStack>
          ),
        },
      ]}
    />
  );
}
